export type GoogleCredentialResponse = {
  credential?: string;
};

export type GoogleButtonConfiguration = {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: string;
  locale?: string;
};

type GoogleIdConfiguration = {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  context?: 'signin' | 'signup' | 'use';
  itp_support?: boolean;
  use_fedcm_for_button?: boolean;
};

export type GoogleIdentityServices = {
  accounts: {
    id: {
      initialize: (configuration: GoogleIdConfiguration) => void;
      renderButton: (parent: HTMLElement, options: GoogleButtonConfiguration) => void;
    };
  };
};

declare global {
  interface Window {
    google?: GoogleIdentityServices;
  }
}

const googleIdentityScriptId = 'google-identity-services';
const googleIdentityScriptSource = 'https://accounts.google.com/gsi/client';
const googleIdentityScriptTimeoutMs = 10_000;

let googleIdentityServicesPromise: Promise<GoogleIdentityServices> | null = null;
let loadedScriptLocale: string | undefined;

const getGoogleIdentityServices = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const services = window.google;

  if (!services?.accounts?.id) {
    return null;
  }

  return services;
};

const removeExistingScript = () => {
  if (typeof document === 'undefined') {
    return;
  }

  document.getElementById(googleIdentityScriptId)?.remove();
};

export const loadGoogleIdentityServices = (locale?: string): Promise<GoogleIdentityServices> => {
  const availableServices = getGoogleIdentityServices();

  if (availableServices && loadedScriptLocale === locale) {
    return Promise.resolve(availableServices);
  }

  if (typeof document === 'undefined') {
    return Promise.reject(new Error('Google Identity Services is unavailable.'));
  }

  if (googleIdentityServicesPromise && loadedScriptLocale === locale) {
    return googleIdentityServicesPromise;
  }

  loadedScriptLocale = locale;
  googleIdentityServicesPromise = new Promise<GoogleIdentityServices>((resolve, reject) => {
    removeExistingScript();

    const script = document.createElement('script');
    const timeoutId = window.setTimeout(() => {
      reject(new Error('Google Identity Services failed to load.'));
    }, googleIdentityScriptTimeoutMs);

    const handleLoad = () => {
      window.clearTimeout(timeoutId);
      const services = getGoogleIdentityServices();

      if (!services) {
        reject(new Error('Google Identity Services failed to initialize.'));
        return;
      }

      resolve(services);
    };

    const handleError = () => {
      window.clearTimeout(timeoutId);
      reject(new Error('Google Identity Services failed to load.'));
    };

    script.id = googleIdentityScriptId;
    script.src = locale
      ? `${googleIdentityScriptSource}?hl=${encodeURIComponent(locale)}`
      : googleIdentityScriptSource;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    googleIdentityServicesPromise = null;
    loadedScriptLocale = undefined;
    removeExistingScript();
    throw error;
  });

  return googleIdentityServicesPromise;
};
